import { ModuleTypeEnum } from '../module';
import type { AccountType } from '../organization';
import type { TodayWidgetSize } from '../user-preferences';

export type WidgetColumnType = 'main' | 'sidebar';

/**
 * Rough category a Today widget belongs to, used to give a *freshly generated
 * default layout* a sensible reading order:
 *
 * - `now`       — what is happening in the next few hours (schedule, inbox, timer)
 * - `work`      — the things you act on today (your work, notes, shortcuts)
 * - `workspace` — ambient context about everyone else (team, timeline, activity)
 *
 * An ordering hint only — Today renders one flat grid with no section
 * headings, and the persisted layout is a single ordered list the user is free
 * to rearrange however they like.
 */
export type TodayWidgetLayer = 'now' | 'work' | 'workspace';

/** Order the layers seed a default layout in, first to last. */
export const TODAY_WIDGET_LAYER_ORDER: readonly TodayWidgetLayer[] = [
  'now',
  'work',
  'workspace',
];

/** Layer used for a definition that doesn't declare one. */
export const DEFAULT_TODAY_WIDGET_LAYER: TodayWidgetLayer = 'work';

export interface TodayWidgetDefinition {
  id: string;
  label: string;
  description?: string;
  moduleType?: ModuleTypeEnum;
  /**
   * Restrict this widget to specific account types. Absent = available to all
   * account types. Use for business widgets (time tracking, team) that have no
   * meaning on a personal account.
   */
  accountTypes?: AccountType[];
  defaultColumn: string;
  allowedColumns: string[];
  /** Legacy hint used for migration / default size selection. */
  columnType: WidgetColumnType;
  /**
   * Ordering hint for default Today layouts — not a rendered section. Absent =
   * {@link DEFAULT_TODAY_WIDGET_LAYER}. See {@link TodayWidgetLayer}.
   */
  layer?: TodayWidgetLayer;
  /** Default size when the widget is added to the dashboard. Falls back to columnType. */
  defaultSize?: TodayWidgetSize;
  /** Minimum size the user can shrink the widget to. Defaults to 'small'. */
  minSize?: TodayWidgetSize;
  /** Maximum size the user can grow the widget to. Defaults to 'large'. */
  maxSize?: TodayWidgetSize;
  isDefault?: boolean; // Whether this widget should be enabled by default for new users
  isActive?: boolean; // Whether this widget is available for users to add (defaults to true)
  icon: string; // Lucide icon name for the widget
}
